import { type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type TaskDodView } from '@apm/contracts';
import { errorMessage } from '../../../lib/error-message';
import { apiFetch } from '../../../lib/http';
import { cn } from '../../../lib/utils';

const dodKeys = (taskId: string) => ['task', taskId, 'definition-of-done'] as const;

/**
 * Checklist Definition of Done của một task — APM-AGILE-d.
 *
 * ## Vì sao nằm trong drawer, không phải một hộp thoại lúc kéo thẻ
 *
 * Kéo một thẻ sang "Hoàn thành" mà bị một checklist chặn giữa đường là đúng thứ
 * làm đội tắt tính năng. Ở đây thì nó nằm cạnh mô tả, bình luận và tệp đính
 * kèm — tức cạnh **bối cảnh** cần để trả lời từng mục.
 *
 * Server vẫn chặn (`409` khi chưa tick hết), nhưng người dùng gặp checklist
 * **trước** lúc đó, ở nơi họ đang làm việc.
 *
 * ## ⚠️ Không hiện gì khi dự án chưa đặt mục nào
 *
 * Rỗng là trạng thái bình thường của phần lớn dự án. Một khung "Definition of
 * Done (0)" trống trơn trong mọi task là tiếng ồn, và nó gợi ý rằng người dùng
 * đang thiếu một bước.
 */
export function TaskDodChecklist({
  taskId,
  canEdit,
}: {
  readonly taskId: string;
  readonly canEdit: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const checklist = useQuery({
    queryKey: dodKeys(taskId),
    queryFn: () => apiFetch<TaskDodView>(`/tasks/${taskId}/definition-of-done`),
  });

  const toggle = useMutation({
    mutationFn: ({ itemId, checked }: { itemId: string; checked: boolean }) =>
      apiFetch<TaskDodView>(`/tasks/${taskId}/definition-of-done/${itemId}`, {
        method: 'PUT',
        body: { checked },
      }),
    onError: (error) => toast.error(errorMessage(error, t)),
    onSuccess: (view) => queryClient.setQueryData(dodKeys(taskId), view),
  });

  const items = checklist.data?.items ?? [];
  if (items.length === 0) return null;

  const done = items.filter((item) => item.checked).length;
  const required = items.filter((item) => item.isActive).length;

  return (
    <section className="flex flex-col gap-2">
      <header className="flex items-baseline gap-2">
        <h4 className="text-sm font-medium">{t('dod.title')}</h4>
        {/* Con số ngay cạnh tiêu đề: nó là thứ trả lời "còn thiếu mấy mục" mà
            không phải đếm bằng mắt qua một danh sách. */}
        <span className="text-xs tabular-nums text-muted-foreground">
          {done}/{required}
        </span>
      </header>

      <ul className="flex flex-col gap-1">
        {items.map((item) => (
          <li key={item.id} className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="ios-check"
              checked={item.checked}
              // Mục đã tắt chỉ còn để tra ngược — không tick lại được, vì nó
              // không còn là một phần của "xong" nữa.
              disabled={!canEdit || !item.isActive || toggle.isPending}
              aria-label={item.text}
              onChange={(event) =>
                toggle.mutate({ itemId: item.id, checked: event.target.checked })
              }
            />
            <span className={cn('min-w-0 flex-1', !item.isActive && 'text-muted-foreground')}>
              {item.text}
              {item.isActive ? null : <span className="ml-1 text-xs">({t('dod.retired')})</span>}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
